import { useEffect, useRef, useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import type { App } from '@/types/app';
import type { CatalogStack } from '@/types/catalog';
import type { EndpointWithResources } from '@/types/endpoint';
import { toast } from 'sonner';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';

interface ScaleAppDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  app: App | null;
  onSuccess?: () => void;
}

export function ScaleAppDialog({ open, onOpenChange, app, onSuccess }: ScaleAppDialogProps) {
  const [stacks, setStacks] = useState<CatalogStack[]>([]);
  const [selectedStackName, setSelectedStackName] = useState('');
  const [endpoint, setEndpoint] = useState<EndpointWithResources | null>(null);
  // A dialog mounted open with an app that has a catalog starts in the loading state, as the old effect did.
  const [loading, setLoading] = useState(() => open && !!app?.app_catalog?.id);
  const [scaling, setScaling] = useState(false);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();

  const appId = app?.id;
  const catalogId = app?.app_catalog?.id;
  const endpointId = app?.endpoint?.id;

  // Opening the dialog or switching app resets the selection and starts loading (during render, not in the effect).
  const [synced, setSynced] = useState({ open, appId });
  if (synced.open !== open || synced.appId !== appId) {
    setSynced({ open, appId });
    if (open && app) {
      setSelectedStackName('');
      if (catalogId) setLoading(true);
    }
  }

  // Latest catalog/endpoint ids, read by the fetch effect so it re-runs only on open or app change.
  const idsRef = useRef({ catalogId, endpointId });
  useEffect(() => {
    idsRef.current = { catalogId, endpointId };
  });

  useEffect(() => {
    const { catalogId: targetCatalogId, endpointId: targetEndpointId } = idsRef.current;
    if (!open || appId === undefined || !targetCatalogId) return;
    const fetchStacksAndEndpoint = async () => {
      try {
        const stackData = await api.catalog.stacks.list(targetCatalogId);
        setStacks(stackData.filter(s => s.is_active));

        if (targetEndpointId) {
          const endpoints = await api.endpoints.listWithResourcesAll();
          const ep = endpoints.find(e => e.id === targetEndpointId);
          setEndpoint(ep || null);
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    void fetchStacksAndEndpoint();
  }, [open, appId]);

  const currentStack = app?.stack;
  const targetStack = stacks.find(s => s.name === selectedStackName);

  const resourceComparison = useMemo(() => {
    if (!currentStack || !targetStack) return null;

    const cpuDiff = targetStack.cpu_limit - currentStack.cpu_limit;
    const memDiff = targetStack.memory_mb - currentStack.memory_mb;
    const storageDiff = targetStack.storage_gb - currentStack.storage_gb;

    return {
      cpu: { current: currentStack.cpu_limit, new: targetStack.cpu_limit, diff: cpuDiff },
      memory: { current: currentStack.memory_mb, new: targetStack.memory_mb, diff: memDiff },
      storage: { current: currentStack.storage_gb, new: targetStack.storage_gb, diff: storageDiff },
    };
  }, [currentStack, targetStack]);

  const hasInsufficientResources = useMemo(() => {
    if (!endpoint?.resources || !targetStack || !resourceComparison) return false;
    return (
      endpoint.resources.cpu_available < Math.abs(resourceComparison.cpu.diff) ||
      endpoint.resources.memory_available_mb < Math.abs(resourceComparison.memory.diff) ||
      endpoint.resources.storage_available_gb < Math.abs(resourceComparison.storage.diff)
    );
  }, [endpoint, targetStack, resourceComparison]);

  const canScale = useMemo(() => {
    if (!selectedStackName || !targetStack) return false;
    if (targetStack.name === currentStack?.name) return false;
    if (app?.status === 'pending') return false;
    return true;
  }, [selectedStackName, targetStack, currentStack, app?.status]);

  const handleScale = async () => {
    if (!app || !selectedStackName) return;

    try {
      setScaling(true);
      await api.apps.scale(app.id, selectedStackName);
      toast.success(`Scaling to ${selectedStackName}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to scale';
      toast.error(msg);
    } finally {
      setScaling(false);
    }
  };

  const formatDiff = (diff: number, unit: string) => {
    const sign = diff > 0 ? '+' : '';
    const arrow = diff > 0 ? '↑' : '↓';
    const color = diff > 0 ? 'text-green-500' : 'text-red-500';
    return (
      <span className={color}>
        {sign}{diff} {unit} {arrow}
      </span>
    );
  };

  if (!app) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl"
        onOpenAutoFocus={() => captureOpener()}
        onCloseAutoFocus={restoreFocus}
      >
        <DialogHeader>
          <DialogTitle>Scale App: {app.name}</DialogTitle>
          <DialogDescription>
            Change the resource allocation for this app.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-4">
            {currentStack && (
              <div className="rounded-md border p-3 bg-muted/50">
                <p className="text-sm font-medium mb-2">Current Stack: {currentStack.name}</p>
                <div className="flex gap-2">
                  <Badge variant="outline">{currentStack.cpu_limit} cores</Badge>
                  <Badge variant="outline">{currentStack.memory_mb} MB</Badge>
                  <Badge variant="outline">{currentStack.storage_gb} GB</Badge>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="targetStack">Target Stack</Label>
              <select
                id="targetStack"
                value={selectedStackName}
                onChange={(e) => setSelectedStackName(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2"
              >
                <option value="">Select a stack...</option>
                {stacks.map(s => (
                  <option
                    key={s.id}
                    value={s.name}
                    disabled={s.name === currentStack?.name}
                  >
                    {s.name} ({s.cpu_limit}c, {s.memory_mb}MB, {s.storage_gb}GB)
                    {s.name === currentStack?.name ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {resourceComparison && (
              <div className="rounded-md border p-3">
                <p className="text-sm font-medium mb-3">Resource Changes:</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground">
                      <th className="text-left">Resource</th>
                      <th className="text-center">Current</th>
                      <th className="text-center">New</th>
                      <th className="text-right">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>CPU</td>
                      <td className="text-center">{resourceComparison.cpu.current} cores</td>
                      <td className="text-center">{resourceComparison.cpu.new} cores</td>
                      <td className="text-right">{formatDiff(resourceComparison.cpu.diff, 'cores')}</td>
                    </tr>
                    <tr>
                      <td>Memory</td>
                      <td className="text-center">{resourceComparison.memory.current} MB</td>
                      <td className="text-center">{resourceComparison.memory.new} MB</td>
                      <td className="text-right">{formatDiff(resourceComparison.memory.diff, 'MB')}</td>
                    </tr>
                    <tr>
                      <td>Storage</td>
                      <td className="text-center">{resourceComparison.storage.current} GB</td>
                      <td className="text-center">{resourceComparison.storage.new} GB</td>
                      <td className="text-right">{formatDiff(resourceComparison.storage.diff, 'GB')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {hasInsufficientResources && endpoint && (
              <div className="rounded-md border border-red-500 p-3 text-sm text-red-500">
                <p className="font-medium">Insufficient resources on endpoint '{endpoint.name}'</p>
                <p className="mt-1">
                  Available: {endpoint.resources?.cpu_available}c, {endpoint.resources?.memory_available_mb}MB, {endpoint.resources?.storage_available_gb}GB
                </p>
              </div>
            )}

            {app.status === 'pending' && (
              <div className="rounded-md border border-yellow-500 p-3 text-sm text-yellow-600">
                App has pending deployment. Wait for completion before scaling.
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleScale}
                disabled={!canScale || scaling}
              >
                {scaling ? 'Scaling...' : `Scale to ${selectedStackName || '...'}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}